import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  TrashIcon,
  BeakerIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

import { feedStockApi, FeedStock } from '../api/feedStock';
import { CreateDailyFeedMixDto } from '../api/nutrition';
import { cagesApi, type CageOption } from '../api/cages';
import DecimalInput from './ui/DecimalInput';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface DailyFeedMixFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateDailyFeedMixDto) => Promise<void>;
  initialData?: Partial<CreateDailyFeedMixDto>;
  editMode?: boolean;
}

interface FeedComponent {
  feedStockId: string;
  quantity: number;
  notes?: string;
}

export default function DailyFeedMixForm({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  editMode = false
}: DailyFeedMixFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [feedStocks, setFeedStocks] = useState<FeedStock[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [nutritionalPreview, setNutritionalPreview] = useState<any>(null);
  const [cageOptions, setCageOptions] = useState<CageOption[]>([]);

  // Form data
  const [formData, setFormData] = useState({
    feedDate: format(new Date(), 'yyyy-MM-dd'),
    feedComponents: [] as FeedComponent[],
    location: '',
    operator: '',
    notes: '',
    birdCount: 0,
    feedingTimes: [] as string[],
    weatherConditions: '',
    specialNotes: '',
    tags: [] as string[]
  });

  // Load feed stocks
  useEffect(() => {
    const loadFeedStocks = async () => {
      try {
        const response = await feedStockApi.getFeedStocks();
        setFeedStocks(response.data);
      } catch (err: any) {
        setError('Failed to load feed stocks');
      }
    };
    loadFeedStocks();
  }, []);

  useEffect(() => {
    cagesApi
      .options(true)
      .then(setCageOptions)
      .catch(() => setCageOptions([]));
  }, []);

  // Initialize form data
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        feedDate: initialData.feedDate || format(new Date(), 'yyyy-MM-dd'),
        feedComponents: initialData.feedComponents || [],
        feedingTimes: initialData.feedingTimes || [],
        tags: initialData.tags || [],
        notes: initialData.notes || '',
        specialNotes: initialData.specialNotes || '',
        weatherConditions: initialData.weatherConditions || '',
        location: initialData.location || '',
        operator: initialData.operator || ''
      }));
    }
  }, [initialData]);

  // Calculate nutritional preview when components change
  useEffect(() => {
    if (formData.feedComponents.length > 0) {
      calculateNutritionalPreview();
    } else {
      setNutritionalPreview(null);
    }
  }, [formData.feedComponents]);

  const calculateNutritionalPreview = async () => {
    if (formData.feedComponents.length === 0) return;

    setIsCalculating(true);
    try {
      // Calculate rough nutritional preview (simplified version)
      const totalQuantity = formData.feedComponents.reduce((sum, comp) => sum + comp.quantity, 0);
      let totalProtein = 0;
      let totalFat = 0;
      let totalFiber = 0;
      let totalCarbohydrate = 0;
      let totalCost = 0;

      formData.feedComponents.forEach(comp => {
        const feedStock = feedStocks.find(fs => fs.id === comp.feedStockId);
        if (feedStock) {
          const nutrition = feedStock.nutritionalInfo || {};
          const unitPrice = parseFloat(feedStock.unitPrice.toString()) || 0;
          
          totalProtein += ((nutrition.protein || 0) / 1000) * comp.quantity;
          totalFat += ((nutrition.fat || 0) / 1000) * comp.quantity;
          totalFiber += ((nutrition.fiber || 0) / 1000) * comp.quantity;
          totalCarbohydrate += ((nutrition.carbohydrate || 0) / 1000) * comp.quantity;
          totalCost += unitPrice * comp.quantity;
        }
      });

      setNutritionalPreview({
        totalQuantity,
        totalCost,
        proteinPercentage: totalQuantity > 0 ? (totalProtein / totalQuantity) * 100 : 0,
        fatPercentage: totalQuantity > 0 ? (totalFat / totalQuantity) * 100 : 0,
        fiberPercentage: totalQuantity > 0 ? (totalFiber / totalQuantity) * 100 : 0,
        carbohydratePercentage: totalQuantity > 0 ? (totalCarbohydrate / totalQuantity) * 100 : 0,
        components: formData.feedComponents.map(comp => {
          const feedStock = feedStocks.find(fs => fs.id === comp.feedStockId);
          return {
            ...comp,
            name: feedStock?.name || 'Unknown',
            percentage: totalQuantity > 0 ? (comp.quantity / totalQuantity) * 100 : 0
          };
        })
      });
    } catch (err: any) {
      console.error('Failed to calculate nutritional preview:', err);
    } finally {
      setIsCalculating(false);
    }
  };

  const addFeedComponent = () => {
    setFormData(prev => ({
      ...prev,
      feedComponents: [
        ...prev.feedComponents,
        { feedStockId: '', quantity: 0, notes: '' }
      ]
    }));
  };

  const removeFeedComponent = (index: number) => {
    setFormData(prev => ({
      ...prev,
      feedComponents: prev.feedComponents.filter((_, i) => i !== index)
    }));
  };

  const updateFeedComponent = (index: number, field: keyof FeedComponent, value: any) => {
    setFormData(prev => ({
      ...prev,
      feedComponents: prev.feedComponents.map((comp, i) => 
        i === index ? { ...comp, [field]: value } : comp
      )
    }));
  };

  const addFeedingTime = () => {
    setFormData(prev => ({
      ...prev,
      feedingTimes: [...prev.feedingTimes, '']
    }));
  };

  const updateFeedingTime = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      feedingTimes: prev.feedingTimes.map((time, i) => i === index ? value : time)
    }));
  };

  const removeFeedingTime = (index: number) => {
    setFormData(prev => ({
      ...prev,
      feedingTimes: prev.feedingTimes.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.feedComponents.length === 0) {
      setError('Please add at least one feed component');
      return;
    }

    const totalQuantity = formData.feedComponents.reduce((sum, comp) => sum + comp.quantity, 0);
    if (totalQuantity === 0) {
      setError('Total quantity must be greater than 0');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const submitData: CreateDailyFeedMixDto = {
        feedDate: formData.feedDate,
        feedComponents: formData.feedComponents,
        location: formData.location,
        operator: formData.operator,
        notes: formData.notes,
        birdCount: formData.birdCount,
        feedingTimes: formData.feedingTimes.filter(time => time.trim() !== ''),
        weatherConditions: formData.weatherConditions,
        specialNotes: formData.specialNotes,
        tags: formData.tags
      };

      await onSubmit(submitData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create feed mix');
    } finally {
      setIsLoading(false);
    }
  };

  const getQualityIndicator = (percentage: number, type: 'protein' | 'fat' | 'fiber' | 'carbohydrate') => {
    let isGood = false;
    let color = 'text-red-600';

    switch (type) {
      case 'protein':
        isGood = percentage >= 14 && percentage <= 22;
        color = isGood ? 'text-green-600' : percentage >= 10 ? 'text-yellow-600' : 'text-red-600';
        break;
      case 'fat':
        isGood = percentage >= 3 && percentage <= 8;
        color = isGood ? 'text-green-600' : percentage >= 1 ? 'text-yellow-600' : 'text-red-600';
        break;
      case 'fiber':
        isGood = percentage <= 18;
        color = isGood ? 'text-green-600' : percentage <= 25 ? 'text-yellow-600' : 'text-red-600';
        break;
      case 'carbohydrate':
        isGood = percentage >= 20 && percentage <= 60;
        color = isGood ? 'text-green-600' : percentage >= 10 ? 'text-yellow-600' : 'text-red-600';
        break;
    }

    return (
      <div className="flex items-center">
        <span className={`text-sm font-medium ${color}`}>
          {percentage.toFixed(1)}%
        </span>
        {isGood ? (
          <CheckIcon className="h-4 w-4 text-green-500 ml-1" />
        ) : (
          <ExclamationTriangleIcon className="h-4 w-4 text-red-500 ml-1" />
        )}
      </div>
    );
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-7xl" panelClassName="overflow-hidden">
      <BrandModalHeader
        title={editMode ? 'Edit Daily Feed Mix' : 'Create Daily Feed Mix'}
        subtitle={editMode ? 'Update the feed mix details' : 'Create a new daily feed mix with nutrition management'}
        onClose={onClose}
      />
      <BrandModalBody className="max-h-[70vh] overflow-y-auto">
                    {error && (
                      <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                          <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-8">
                      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                        {/* Left Column - Basic Information */}
                        <div className="space-y-6">
                          <div>
                            <h4 className="text-lg font-medium text-slate-900 mb-4">Basic Information</h4>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Feed Date</label>
                                <input
                                  type="date"
                                  value={formData.feedDate}
                                  onChange={(e) => setFormData(prev => ({ ...prev, feedDate: e.target.value }))}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Location</label>
                                <select
                                  value={formData.location}
                                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                >
                                  <option value="">Select location...</option>
                                  <option value="Main Aviary">Main Aviary</option>
                                  <option value="Breeding Area">Breeding Area</option>
                                  <option value="Quarantine">Quarantine</option>
                                  <option value="Outdoor Flight">Outdoor Flight</option>
                                  {cageOptions.map((cage) => (
                                    <option key={cage.id} value={cage.cageNumber}>
                                      Cage {cage.cageNumber}{cage.location ? ` · ${cage.location}` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Operator</label>
                                <input
                                  type="text"
                                  value={formData.operator}
                                  onChange={(e) => setFormData(prev => ({ ...prev, operator: e.target.value }))}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                  placeholder="Person responsible for feeding"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Bird Count</label>
                                <DecimalInput
                                  value={formData.birdCount}
                                  onChange={(value) => setFormData(prev => ({ ...prev, birdCount: Math.floor(value ?? 0) }))}
                                  min={0}
                                  integer
                                  placeholder="Number of birds"
                                  inputClassName="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Additional Details */}
                          <div>
                            <h4 className="text-lg font-medium text-slate-900 mb-4">Additional Details</h4>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Weather Conditions</label>
                                <input
                                  type="text"
                                  value={formData.weatherConditions || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, weatherConditions: e.target.value }))}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                  placeholder="e.g., Sunny, 22\u00b0C, Humid"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Notes</label>
                                <textarea
                                  value={formData.notes || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                  rows={3}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                  placeholder="General notes about this feed mix..."
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700">Special Notes</label>
                                <textarea
                                  value={formData.specialNotes || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, specialNotes: e.target.value }))}
                                  rows={2}
                                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                                  placeholder="Any special observations or notes..."
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Middle Column - Feed Components */}
                        <div>
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-lg font-medium text-slate-900">Feed Components</h4>
                            <button
                              type="button"
                              onClick={addFeedComponent}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-cyan-700 bg-cyan-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
                            >
                              <PlusIcon className="h-4 w-4 mr-1" />
                              Add Component
                            </button>
                          </div>
                          
                          <div className="space-y-4">
                            {formData.feedComponents.map((component, index) => (
                              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-slate-50/80">
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Feed Stock</label>
                                    <select
                                      value={component.feedStockId}
                                      onChange={(e) => updateFeedComponent(index, 'feedStockId', e.target.value)}
                                      className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                                      required
                                    >
                                      <option key="empty-option-1753461297294" value="">Select feed stock...</option>
                                      {feedStocks.map(stock => (
                                        <option key={stock.id} value={stock.id}>
                                          {stock.name} ({stock.quantity} {stock.unit} available)
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-1">Quantity (kg)</label>
                                      <DecimalInput
                                        value={component.quantity}
                                        onChange={(value) => updateFeedComponent(index, 'quantity', value ?? 0)}
                                        min={0}
                                        placeholder="0.0"
                                        required
                                        inputClassName="block w-full input border-gray-300 shadow-sm sm:text-sm"
                                      />
                                    </div>
                                    <div className="flex items-end">
                                      <button
                                        type="button"
                                        onClick={() => removeFeedComponent(index)}
                                        className="w-full inline-flex items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                      >
                                        <TrashIcon className="h-4 w-4 mr-1" />
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Notes (optional)</label>
                                    <input
                                      type="text"
                                      value={component.notes || ''}
                                      onChange={(e) => updateFeedComponent(index, 'notes', e.target.value)}
                                      className="block w-full input border-gray-300 shadow-sm sm:text-sm"
                                      placeholder="Notes about this component..."
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                            
                            {formData.feedComponents.length === 0 && (
                              <div className="text-center py-12 text-slate-500 border-2 border-dashed border-gray-300 rounded-lg">
                                <BeakerIcon className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                                <p className="text-sm font-medium">No feed components added yet</p>
                                <p className="text-xs">Click "Add Component" to start building your feed mix</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right Column - Nutritional Preview */}
                        <div className="bg-slate-50/80 rounded-lg p-6">
                          <h4 className="text-lg font-medium text-slate-900 mb-4">Nutritional Preview</h4>
                          
                          {isCalculating && (
                            <div className="flex items-center justify-center py-12">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
                              <span className="ml-2 text-sm text-slate-600">Calculating...</span>
                            </div>
                          )}

                          {nutritionalPreview && (
                            <div className="space-y-4">
                              {/* Summary */}
                              <div className="bg-white rounded-lg p-4 shadow-sm">
                                <h5 className="font-medium text-slate-900 mb-3">Summary</h5>
                                <dl className="space-y-2">
                                  <div className="flex justify-between">
                                    <dt className="text-sm text-slate-500">Total Quantity:</dt>
                                    <dd className="text-sm font-medium text-slate-900">
                                      {nutritionalPreview.totalQuantity.toFixed(1)} kg
                                    </dd>
                                  </div>
                                  <div className="flex justify-between">
                                    <dt className="text-sm text-slate-500">Estimated Cost:</dt>
                                    <dd className="text-sm font-medium text-slate-900">
                                      R{nutritionalPreview.totalCost.toFixed(2)}
                                    </dd>
                                  </div>
                                  <div className="flex justify-between">
                                    <dt className="text-sm text-slate-500">Cost per kg:</dt>
                                    <dd className="text-sm font-medium text-slate-900">
                                      R{nutritionalPreview.totalQuantity > 0 ? (nutritionalPreview.totalCost / nutritionalPreview.totalQuantity).toFixed(2) : '0.00'}
                                    </dd>
                                  </div>
                                </dl>
                              </div>

                              {/* Nutritional Breakdown */}
                              <div className="bg-white rounded-lg p-4 shadow-sm">
                                <h5 className="font-medium text-slate-900 mb-3">Nutritional Breakdown</h5>
                                <dl className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <dt className="text-sm text-slate-500">Protein:</dt>
                                    <dd>
                                      {getQualityIndicator(nutritionalPreview.proteinPercentage, 'protein')}
                                    </dd>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <dt className="text-sm text-slate-500">Fat:</dt>
                                    <dd>
                                      {getQualityIndicator(nutritionalPreview.fatPercentage, 'fat')}
                                    </dd>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <dt className="text-sm text-slate-500">Fiber:</dt>
                                    <dd>
                                      {getQualityIndicator(nutritionalPreview.fiberPercentage, 'fiber')}
                                    </dd>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <dt className="text-sm text-slate-500">Carbohydrate:</dt>
                                    <dd>
                                      {getQualityIndicator(nutritionalPreview.carbohydratePercentage, 'carbohydrate')}
                                    </dd>
                                  </div>
                                </dl>
                              </div>

                              {/* Component Breakdown */}
                              <div className="bg-white rounded-lg p-4 shadow-sm">
                                <h5 className="font-medium text-slate-900 mb-3">Component Breakdown</h5>
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                  {nutritionalPreview.components.map((comp: any, index: number) => (
                                    <div key={index} className="flex justify-between items-center">
                                      <span className="text-sm text-slate-600 truncate mr-2">{comp.name}</span>
                                      <div className="text-right flex-shrink-0">
                                        <div className="text-sm font-medium text-slate-900">
                                          {comp.quantity.toFixed(1)} kg
                                        </div>
                                        <div className="text-xs text-slate-500">
                                          {comp.percentage.toFixed(1)}%
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* Quality Guidelines */}
                              <div className="bg-cyan-50 rounded-lg p-4">
                                <h5 className="font-medium text-blue-900 mb-2">Quality Guidelines</h5>
                                <ul className="text-xs text-cyan-800 space-y-1">
                                  <li>• Protein: 14-22% (optimal for most birds)</li>
                                  <li>• Fat: 3-8% (essential fatty acids)</li>
                                  <li>• Fiber: ≤18% (digestibility)</li>
                                  <li>• Carbohydrate: 20-60% (energy)</li>
                                  <li>• Moisture: ≤12% (storage stability)</li>
                                </ul>
                              </div>
                            </div>
                          )}

                          {!nutritionalPreview && !isCalculating && (
                            <div className="text-center py-12 text-slate-500">
                              <BeakerIcon className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                              <p className="text-sm">Add feed components to see nutritional preview</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </form>

      </BrandModalBody>
      <BrandModalFooter>
                    <button
                      type="button"
                      onClick={onClose}
                      className="btn btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                      <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={isLoading || formData.feedComponents.length === 0}
                        className="btn btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {editMode ? 'Updating...' : 'Creating...'}
                          </>
                        ) : (
                          editMode ? 'Update Feed Mix' : 'Create Feed Mix'
                        )}
                      </button>
      </BrandModalFooter>
    </BrandModal>
  );
} 